import React from 'react';
import { View } from 'react-native';
import { router } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';

import { ThemedText } from '@/components/themed-text';
import { PressableScale } from '@/components/ui/pressable-scale';
import { useProviderPendingRecurrences } from '@/hooks/use-recurrences';
import { RecurrenceSummary } from '@/types/recurrence';
import { recurrencePeriodLabel, recurrenceScheduleSummary } from '@/utils/format-recurrence';
import { useIconChip } from '@/hooks/use-theme-colors';

function RequestLine({ item }: { item: RecurrenceSummary }) {
  return (
    <View className="gap-0.5">
      <ThemedText className="text-sm font-semibold text-neutral-900" numberOfLines={1}>
        {item.client?.user?.username ?? 'Cliente'} · {item.service?.name ?? 'Serviço'}
      </ThemedText>
      <ThemedText className="text-xs text-neutral-500" numberOfLines={1}>
        {recurrenceScheduleSummary(item)} · {recurrencePeriodLabel(item)}
      </ThemedText>
    </View>
  );
}

/**
 * Pedido de recorrência esperando resposta — some quando não há nada pendente.
 * Aparece na agenda e na home para o pedido não ficar escondido atrás do sino.
 */
export function PendingRecurrenceBanner() {
  const chip = useIconChip();
  const { data } = useProviderPendingRecurrences();
  const list = data ?? [];
  if (list.length === 0) return null;

  const preview = list.slice(0, 2);
  const rest = list.length - preview.length;

  return (
    <PressableScale
      haptic
      onPress={() => router.push('/(tabs)/(provider)/schedule/recurrences')}
      className="gap-3 rounded-2xl border border-primary/20 bg-card p-4"
      style={{
        borderCurve: 'continuous',
        boxShadow: '0 1px 2px rgba(23, 17, 10, 0.04), 0 6px 16px rgba(253, 80, 1, 0.10)',
      }}
      accessibilityLabel={`${list.length} pedido(s) de recorrência aguardando resposta`}
    >
      <View className="flex-row items-center gap-3">
        <View
          className="h-11 w-11 items-center justify-center rounded-2xl"
          style={{ backgroundColor: chip('#FD5001', '1A').bg, borderCurve: 'continuous' }}
        >
          <Ionicons name="repeat" size={22} color={chip('#FD5001', '1A').fg} />
        </View>
        <View className="flex-1">
          <ThemedText className="text-base font-bold text-neutral-900">
            {list.length === 1
              ? 'Alguém quer recorrência'
              : `${list.length} pedidos de recorrência`}
          </ThemedText>
          <ThemedText className="text-xs text-neutral-500">
            Aguardando você aprovar ou recusar
          </ThemedText>
        </View>
        <View className="rounded-full bg-primary px-2.5 py-1">
          <ThemedText className="text-xs font-bold text-white">{list.length}</ThemedText>
        </View>
      </View>

      <View className="gap-2 rounded-xl bg-neutral-50 p-3">
        {preview.map((item) => (
          <RequestLine key={item.id} item={item} />
        ))}
        {rest > 0 && (
          <ThemedText className="text-xs text-neutral-400">
            +{rest} {rest === 1 ? 'outro pedido' : 'outros pedidos'}
          </ThemedText>
        )}
      </View>

      <View className="flex-row items-center gap-1">
        <ThemedText className="text-xs font-bold text-primary">Ver pedidos</ThemedText>
        <Ionicons name="chevron-forward" size={14} color="#FD5001" />
      </View>
    </PressableScale>
  );
}
